import type { InputHTMLAttributes } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { renderInputIcon } from '@storefront/lib-global/baseComponents/inputs/Input';
import type { FieldInputProps } from '@storefront/lib-global/baseComponents/inputs/Input';
import {
  blackBorder,
  errorBorder,
  goingUpBaseStyle,
  initialBorder,
  labelStyle,
  noBorder,
} from '@storefront/lib-global/baseComponents/inputs/Input.styles';
import { GiftCardBalanceTooltip } from '@storefront/lib-global/globalComponents/giftCardBalanceTooltip';
import { useInputAnimation } from '@storefront/lib-global/hooks/useInputAnimation';
import { TypographyStyles, colours, media } from '@storefront/lib-global/stylings';

const Ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const S = {
  FieldContainer: styled.div`
    position: relative;

    .error {
      border: solid ${colours.RED} 1px;
    }

    .error-label {
      color: ${colours.RED};
    }

    .grayed-out {
      svg {
        path {
          fill: ${colours.NOBEL_GREY};
        }
      }
    }
  `,
  FieldInput: styled.input<FieldInputProps>`
    background: none;
    ${({ $isInputError }) => (!$isInputError ? blackBorder : noBorder)}
    border-color: ${({ $isEmpty }) => {
      if (!$isEmpty) return colours.BLACK;
      return colours.GREY;
    }};
    border-radius: 0;
    color: ${({ $colour }) => (!$colour ? colours.BLACK : $colour)};
    height: 44px;
    padding: ${({ $isGiftCardBalance }) => ($isGiftCardBalance ? '0 70px 0 12px' : '0 40px 0 12px')};
    width: 100%;
    ${TypographyStyles.Body.Medium.Regular}

    /* Chrome, Safari, Edge, Opera */
    /* stylelint-disable property-no-vendor-prefix */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
      appearance: textfield;
    }

    &::-ms-reveal {
      display: none;
    }

    @media ${media.greaterThan('lg')} {
      height: 50px;
    }

    &:focus {
      ${({ $isInputError }) => {
        if ($isInputError) {
          return errorBorder;
        }

        return initialBorder;
      }}
    }

    &::placeholder {
      color: transparent;
    }
  `,
  FieldLabel: styled.label<{ $isEmpty?: boolean; $disabled?: boolean; $colour?: string }>`
    color: ${({ $isEmpty, $disabled, $colour }) => {
      if ($disabled) {
        return colours.GREY;
      }
      if (!$isEmpty) {
        return colours.BLACK;
      }
      return !$colour ? colours.GREY : $colour;
    }};

    @media ${media.lessThan('sm')} {
      ${Ellipsis}
      width: 200px;
    }

    @media ${media.between('lg', 'xl')} {
      ${({ $disabled }) =>
        $disabled &&
        css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: 150px;
        `}

      ${Ellipsis}
      width: 230px;
    }

    &.going-up {
      ${goingUpBaseStyle}
      transform: translateY(-50%);
    }

    ${labelStyle}

    ${({ $isEmpty }) =>
      !$isEmpty &&
      css`
        ${goingUpBaseStyle}
        transform: translateY(-50%);
        @media ${media.greaterThan('lg')} {
          transform: translateY(-0.3rem);
        }
      `}
  `,
};

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  isInputError?: boolean;
  isEmpty?: boolean;
  register?: UseFormRegisterReturn;
  testId?: string;
  colour?: string;
  label?: string;
  isGiftCardBalance?: boolean;
}

export function GiftCardBalanceInput({
  testId,
  isInputError,
  isEmpty,
  register,
  placeholder,
  colour,
  isGiftCardBalance,
  ...props
}: InputFieldProps) {
  const { className, handleBlur, handleFocus } = useInputAnimation(isEmpty);

  return (
    <S.FieldContainer className={isInputError ? 'error' : ''}>
      <S.FieldLabel $isEmpty={isEmpty} className={`${className} ${isInputError ? 'error-label' : ''}`}>
        {placeholder}
      </S.FieldLabel>
      <S.FieldInput
        {...props}
        {...register}
        placeholder={placeholder?.replace('*', '')}
        data-testid={testId}
        $isInputError={isInputError}
        $isEmpty={isEmpty}
        $colour={colour}
        $isGiftCardBalance={isGiftCardBalance}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />

      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      {renderInputIcon(Boolean(isInputError), !!isEmpty, isGiftCardBalance)}
      <GiftCardBalanceTooltip isInputError={isInputError} />
    </S.FieldContainer>
  );
}
